<template>
	<div class="item">
		<span class="label">Status</span>
		<div v-if="enableEditableStatus" class="status-container">
			<Spinner v-if="isSubmitting" class="small" />
			<select :disabled="isSubmitting" v-model="annualReviewStatusId" @change="updateStatusAsync">
				<option v-for="option in filteredStatuses" :key="option.value" :value="option.value">
					{{ option.name }}
				</option>
			</select>
		</div>
		<div v-else>
			<span :class="['value', annualReview.annualReviewStatusClass]">
				{{ annualReview.annualReviewStatusHumanized }}
			</span>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import Spinner from '@/components/shared/Spinner.vue';
	import { AnnualReviewDetails } from '@/models/loans';
	import { AnnualReviewStatusTypes, StatusTypeOption, AnnualReviewStatusOptionsLookup } from '@/types/annual-review';
	import { Roles } from '@/models/permissions';
	import { useRolesAccess } from '@/composables/usePermissionsAccess';

	const props = defineProps<{
		annualReview: AnnualReviewDetails;
		onReloadAnnualReviewAsync: () => Promise<void>;
	}>();

	const { inRole } = useRolesAccess();

	const isSubmitting = ref<boolean>(false);

	const annualReviewStatusId = ref<number>(props.annualReview.annualReviewStatusId);

	const filteredStatuses = computed(() => filteredAnnualReviewStatuses(props.annualReview.annualReviewStatusId));

	const filteredAnnualReviewStatuses = (annualReviewStatusId: AnnualReviewStatusTypes): StatusTypeOption[] => {
		const allowedStatuses = {
			[AnnualReviewStatusTypes.INITIALLY_PASSED]: [
                AnnualReviewStatusTypes.INITIALLY_PASSED,
				AnnualReviewStatusTypes.APPROVED,
				AnnualReviewStatusTypes.DECLINED
			],
			[AnnualReviewStatusTypes.INITIALLY_FAILED]: [
				AnnualReviewStatusTypes.INITIALLY_FAILED,
				AnnualReviewStatusTypes.DECLINED
			]
		};

		const validStatuses = allowedStatuses[annualReviewStatusId] || [annualReviewStatusId];

		return validStatuses.map((status: number) => AnnualReviewStatusOptionsLookup[status]);
	};

	const enableEditableStatus = computed(() => {
		const validStatuses = [AnnualReviewStatusTypes.INITIALLY_PASSED, AnnualReviewStatusTypes.INITIALLY_FAILED];

		var isAdmin = inRole(Roles.Admin);
		var validOptions = filteredStatuses.value.length > 0;

		return isAdmin && validOptions && validStatuses.includes(props.annualReview.annualReviewStatusId);
	});

	async function updateStatusAsync() {
		isSubmitting.value = true;

		try {
			await axios.post(`/api/annualreview/${props.annualReview.id}/status/${annualReviewStatusId.value}`);

			await props.onReloadAnnualReviewAsync();
		} catch {
			functions.openModal({
				title: 'Oops!',
				description: `Failed to update annual review status. Please try again later.`
			});
		} finally {
			isSubmitting.value = false;
		}
	}
</script>

<style scoped src="@/assets/css/card-rizz.css" />

<style scoped>
	.item {
		align-items: center !important;
	}

	.status-container {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	select {
		text-align: end;
		padding: 8px;
		border-radius: 4px !important;
		border: none;
		height: fit-content;
		font-size: 14px;
		outline: none;
	}

	select:hover {
		background-color: #eee;
	}

	select:focus,
	select:hover {
		border: none !important;
	}

	.spinner.small {
		width: 28px;
		height: 28px;
		flex-shrink: 0;
	}
</style>
